import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { RecipeSearchResult, FieldFilter, SearchEvent, SearchPayload } from '@shared/models/search.model';
import { SearchService } from '@services/search.service';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { Sort } from '@angular/material/sort';
import { DataRef, Domain, Language, Market, ModerationReason } from '@shared/models/dataref.model';
import { PAGE_SIZE } from '@shared/constants/page.constant';
import { RefDataService } from '@services/ref-data.service';
import { ConfigService } from '@services/config.service';
import { CommunitiesRecipesSearchResultComponent } from '../communities-recipes-search-result/communities-recipes-search-result.component';
import { TablePaginatorComponent } from '@shared/components/data-table-manager/table-paginator/table-paginator.component';

@Component({
  selector: 'app-communities-recipes-search',
  templateUrl: './communities-recipes-search.component.html',
  standalone: false,
})
export class CommunitiesRecipesSearchComponent implements OnInit, OnDestroy, OnChanges {
  @Input() options: string[] = [];

  @ViewChild('searchResultComponent') resultComponent?: CommunitiesRecipesSearchResultComponent;

  domains: Subject<Domain[]> = new Subject<Domain[]>();
  markets: Market[] = [];
  languages: Language[] = [];
  status: DataRef[] = [];
  recipeReasons: ModerationReason[] = [];
  searchResult: Subject<RecipeSearchResult> = new Subject<RecipeSearchResult>();
  sort: Sort = { active: 'modificationDate', direction: 'desc' };
  q?: string;
  fieldFilters: FieldFilter[] = [];
  pageIndex = 0;
  pageSize = PAGE_SIZE;

  queryParamLang = '';
  queryParamDomain = '';
  queryParamMarket = '';

  destroy$ = new Subject<void>();
  lastPayload: SearchPayload | undefined;
  lastParam:
    | {
        q?: string;
        size?: number;
        page?: number;
        domain?: string;
        market?: string;
        lang?: string;
        groupBy?: string;
      }
    | undefined;

  constructor(
    private readonly searchService: SearchService,
    private readonly refDataService: RefDataService,
    private readonly configService: ConfigService,
  ) {}

  ngOnInit(): void {
    forkJoin(['domains', 'markets', 'languages', 'statuses', 'recipeReasons'].map((key: string) => this.refDataService.getByKey(key, true)))
      .pipe(
        tap((response) => {
          this.domains.next(this.refDataService.translateReference(this.refDataService.toArray(response[0])));
          this.markets = this.refDataService.translateReference(this.refDataService.toArray(response[1]));
          this.languages = this.refDataService.translateReference(this.refDataService.toArray(response[2]));
          this.status = this.filterStatus(this.refDataService.translateReference(this.refDataService.toArray(response[3])));
          this.recipeReasons = this.refDataService.translateReference(this.refDataService.toArray(response[4]));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.initDomainMarketLang();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.options = changes.options.currentValue;
    }
  }

  handleSearchQuery(event: SearchEvent): void {
    this.q = event.q;
    this.fieldFilters = event.fieldFilters;
    this.pageIndex = 0;
    this.search();
  }

  handleSearchPage(paginator: TablePaginatorComponent): void {
    this.pageIndex = paginator.pageIndex;
    this.pageSize = paginator.pageSize;
    this.search();
  }

  handleSearchSorted(sort: Sort): void {
    this.sort = sort;
    this.search();
  }

  filterStatus(allStatuses: DataRef[]): DataRef[] {
    const ugcStatuses = new Set(['REJECTED_MARKET', 'VALIDATED_MARKET', 'VALIDATED_PARTNER', 'DELETED', 'TO_BE_CORRECTED']);
    return allStatuses.filter((f) => ugcStatuses.has(f.key));
  }

  /**
   * init domain, market, lang when refresh
   */
  initDomainMarketLang(): void {
    const defaultSearchQueryParams = this.configService.getDefaultSearchQueryParams();
    this.queryParamDomain = defaultSearchQueryParams.domain;
    this.queryParamMarket = defaultSearchQueryParams.market;
    this.queryParamLang = defaultSearchQueryParams.lang;
  }

  search(): void {
    const payload = this.buildSearchPayload(this.sort);
    const param = {
      q: this.q,
      size: this.pageSize,
      page: this.pageIndex,
      domain: this.queryParamDomain,
      market: this.queryParamMarket,
      lang: this.queryParamLang,
      groupBy: this.configService.getAppConfig().recipes?.groupBy,
    };
    // search only on change
    if (JSON.stringify(payload) !== JSON.stringify(this.lastPayload) || JSON.stringify(param) !== JSON.stringify(this.lastParam)) {
      // update status list only on change of market, domain
      const domain = payload.fieldFilters?.find((filter) => filter.field === 'domain.key')?.values[0];
      const market = payload.fieldFilters?.find((filter) => filter.field === 'market.key')?.values[0];
      const lastDomain = this.lastPayload?.fieldFilters?.find((filter) => filter.field === 'domain.key')?.values[0];
      const lastMarket = this.lastPayload?.fieldFilters?.find((filter) => filter.field === 'market.key')?.values[0];
      if (lastMarket !== market || lastDomain !== domain) {
        this.refDataService
          .getByKey('statuses', true, domain as string, market as string)
          .subscribe(
            (allStatus) =>
              (this.status = this.filterStatus(this.refDataService.translateReference(this.refDataService.toArray(allStatus)))),
          );
      }

      this.lastPayload = payload;
      this.lastParam = param;
      this.searchService.searchRecipe(payload, param).subscribe((response) => {
        this.searchResult.next(response);
      });
    }
  }

  ngOnDestroy(): void {
    this.domains.complete();
    this.searchResult.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  buildSearchPayload(sort: Sort): SearchPayload {
    const payload: SearchPayload = {
      sort: {
        name: sort.active,
        direction: sort.direction === '' ? 'DESC' : sort.direction,
      },
    };
    if (this.configService.getAppConfig()?.recipes) {
      payload.fieldList = this.configService.getAppConfig().recipes?.fieldList;
      payload.facetList = this.configService.getAppConfig().recipes?.facetList.map((facet) => ({ name: facet }));
      payload.fieldFilters = this.configService.getRecipeExcludedFiltersInConfig();
    }
    if (this.fieldFilters.length > 0) {
      payload.fieldFilters = [...(payload.fieldFilters ?? []), ...this.fieldFilters];
    }
    // Add UGC filter
    const ugcFilter: FieldFilter = { field: 'topRecipe.type.key', values: ['UGC'] };
    payload.fieldFilters = [...(payload.fieldFilters ?? []), ugcFilter];

    return payload;
  }
}
